import { EditorModalType, useComponentEditorContext } from "./ComponentEditorContext";
import { PositionsEditor } from "./PositionsEditor/PositionsEditor";
import { ClickableAreasEditor } from "./ClickableAreasEditor";
import { ClipPathEditor } from "./ClipPathEditor";
import { FilterEditor } from "./FilterEditor";
import React, { useEffect, useMemo, useState } from "react";
import { ModalEditorDisplayContext } from "./ModalEditorContext";
import { ImagePreviewContext } from "./ImagePreviewContext";
import { ImagePopover } from "./ImagePopover";
import { PathProvider } from "./PathInfo";

export function ModalEditorDisplay(props: { children: React.ReactNode }) {
  const { getImageSrc } = useComponentEditorContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPath, setModalPath] = useState<string[]>([]);
  const [modalValue, setModalValue] = useState<object | null>(null);
  const [modalType, setModalType] = useState<EditorModalType>();
  const [selectedImage, setSelectedImage] = useState<string | undefined>(undefined);

  useEffect(() => {
    const closeImagePopover = () => setSelectedImage(undefined);

    document.addEventListener("click", closeImagePopover);
    return () => document.removeEventListener("click", closeImagePopover);
  }, [selectedImage]);

  const selectedImageContext = useMemo(
    () => ({
      setSelectedImage,
    }),
    [setSelectedImage],
  );

  const modalEditorDisplayContext = useMemo(
    () => ({
      onCloseModal: () => setIsModalOpen(false),
      showModal: () => setIsModalOpen(true),
      setModalContext: (key: string[], value: object, type: EditorModalType) => {
        setModalValue(value);
        setModalPath(key);
        setModalType(type);
      },
    }),
    [setIsModalOpen, setModalPath, setModalValue, setModalType],
  );

  return (
    <>
      <ModalEditorDisplayContext.Provider value={modalEditorDisplayContext}>
        <ImagePreviewContext.Provider value={selectedImageContext}>
          {props.children}
          <PathProvider currentPath={modalPath}>
            {modalValue !== null && modalType === EditorModalType.positionsEditor && isModalOpen && (
              <PositionsEditor
                key={modalPath.join("/")}
                showModal={isModalOpen}
                modalContextKey={modalPath}
                modalContextValue={modalValue}
              />
            )}

            {modalValue !== null && modalType === EditorModalType.clickableAreasEditor && isModalOpen && (
              <ClickableAreasEditor
                key={modalPath.join("/")}
                showModal={isModalOpen}
                modalContextKey={modalPath}
                modalContextValue={modalValue}
              />
            )}
            {modalValue !== null && modalType === EditorModalType.clipPathEditor && isModalOpen && (
              <ClipPathEditor
                key={modalPath.join("/")}
                showModal={isModalOpen}
                modalContextKey={modalPath}
                modalContextValue={modalValue}
              />
            )}
            {modalValue !== null && modalType === EditorModalType.filterEditor && isModalOpen && (
              <FilterEditor
                key={modalPath.join("/")}
                showModal={isModalOpen}
                modalContextKey={modalPath}
                modalContextValue={modalValue}
              />
            )}
          </PathProvider>
        </ImagePreviewContext.Provider>
        {selectedImage && (
          <ImagePopover imageName={selectedImage} imageSrc={getImageSrc({ type: "src", value: selectedImage })} />
        )}
      </ModalEditorDisplayContext.Provider>
    </>
  );
}
